import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded'
import StarRoundedIcon from '@mui/icons-material/StarRounded'
import { Box, IconButton, Skeleton, Stack, TextField, Tooltip } from '@mui/material'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import boardApi from '../api/boardApi'
import EmojiPicker from '../components/common/EmojiPicker'
import Kanban from '../components/common/Kanban'
import ConfirmDialog from '../components/common/ConfirmDialog'
import { useToast } from '../components/common/ToastProvider'
import { setBoards } from '../redux/features/boardSlice'
import { setFavouriteList } from '../redux/features/favouriteSlice'
import { errorMessage } from '../utils/errors'

const inlineFieldSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'transparent',
    paddingInline: 0,
    '& fieldset': { border: 'none' }
  },
  '& .MuiOutlinedInput-input': { padding: 0 }
}

const Board = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const toast = useToast()
  const { boardId } = useParams()

  const boards = useSelector((state) => state.board.value)
  const favouriteList = useSelector((state) => state.favourites.value)

  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [sections, setSections] = useState([])
  const [isFavourite, setIsFavourite] = useState(false)
  const [icon, setIcon] = useState('')
  const [loading, setLoading] = useState(true)
  const [confirmOpen, setConfirmOpen] = useState(false)

  const timers = useRef({})

  useEffect(() => {
    const currentTimers = timers.current
    return () => Object.values(currentTimers).forEach(clearTimeout)
  }, [])

  useEffect(() => {
    let active = true
    setLoading(true)

    const getBoard = async () => {
      try {
        const res = await boardApi.getOne(boardId)
        if (!active) return
        setTitle(res.title)
        setDescription(res.description)
        setSections(res.sections || [])
        setIsFavourite(Boolean(res.favourite))
        setIcon(res.icon)
        setLoading(false)
      } catch (err) {
        if (!active) return
        toast.error(errorMessage(err, 'Could not load that board.'))
        navigate('/boards', { replace: true })
      }
    }

    getBoard()
    return () => { active = false }
  }, [boardId, navigate, toast])

  const syncBoard = useCallback((patch) => {
    dispatch(setBoards(boards.map((item) => (item.id === boardId ? { ...item, ...patch } : item))))
    if (isFavourite) {
      dispatch(setFavouriteList(
        favouriteList.map((item) => (item.id === boardId ? { ...item, ...patch } : item))
      ))
    }
  }, [boards, favouriteList, boardId, isFavourite, dispatch])

  const debouncedUpdate = (key, payload) => {
    clearTimeout(timers.current[key])
    timers.current[key] = setTimeout(async () => {
      try {
        await boardApi.update(boardId, payload)
      } catch (err) {
        toast.error(errorMessage(err, 'Could not save your changes.'))
      }
    }, 500)
  }

  const onIconChange = async (newIcon) => {
    setIcon(newIcon)
    syncBoard({ icon: newIcon })
    try {
      await boardApi.update(boardId, { icon: newIcon })
    } catch (err) {
      toast.error(errorMessage(err, 'Could not save the board icon.'))
    }
  }

  const updateTitle = (event) => {
    const newTitle = event.target.value
    setTitle(newTitle)
    syncBoard({ title: newTitle })
    debouncedUpdate('title', { title: newTitle })
  }

  const updateDescription = (event) => {
    const newDescription = event.target.value
    setDescription(newDescription)
    debouncedUpdate('description', { description: newDescription })
  }

  const addFavourite = async () => {
    const nextFavourite = !isFavourite
    try {
      const board = await boardApi.update(boardId, { favourite: nextFavourite })
      if (nextFavourite) {
        dispatch(setFavouriteList([board, ...favouriteList]))
      } else {
        dispatch(setFavouriteList(favouriteList.filter((item) => item.id !== boardId)))
      }
      setIsFavourite(nextFavourite)
    } catch (err) {
      toast.error(errorMessage(err, 'Could not update your favourites.'))
    }
  }

  const deleteBoard = async () => {
    try {
      await boardApi.delete(boardId)
      dispatch(setFavouriteList(favouriteList.filter((item) => item.id !== boardId)))

      const remaining = boards.filter((item) => item.id !== boardId)
      dispatch(setBoards(remaining))
      setConfirmOpen(false)

      if (remaining.length === 0) {
        navigate('/boards', { replace: true })
      } else {
        navigate(`/boards/${remaining[0].id}`, { replace: true })
      }
    } catch (err) {
      toast.error(errorMessage(err, 'Could not delete the board.'))
    }
  }

  return (
    <>
      <Box sx={{ px: { xs: 2, md: 4 }, pt: { xs: 2, md: 3 }, pb: 1.5, flexShrink: 0 }}>
        <Stack direction='row' alignItems='flex-start' spacing={{ xs: 1, md: 2 }}>
          <Box sx={{ pt: 0.5 }}>
            <EmojiPicker icon={icon} onChange={onIconChange} />
          </Box>

          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            {loading ? (
              <>
                <Skeleton variant='rounded' height={38} width='55%' />
                <Skeleton variant='rounded' height={18} width='75%' sx={{ mt: 1 }} />
              </>
            ) : (
              <>
                <TextField
                  value={title}
                  onChange={updateTitle}
                  placeholder='Untitled'
                  variant='outlined'
                  fullWidth
                  aria-label='Board title'
                  sx={{
                    ...inlineFieldSx,
                    '& .MuiOutlinedInput-root': {
                      ...inlineFieldSx['& .MuiOutlinedInput-root'],
                      fontFamily: "'Outfit', sans-serif",
                      fontSize: { xs: '1.5rem', md: '1.875rem' },
                      fontWeight: 600,
                      letterSpacing: '-0.02em'
                    }
                  }}
                />
                <TextField
                  value={description}
                  onChange={updateDescription}
                  placeholder='Add a description'
                  variant='outlined'
                  multiline
                  maxRows={3}
                  fullWidth
                  aria-label='Board description'
                  sx={{
                    ...inlineFieldSx,
                    '& .MuiOutlinedInput-root': {
                      ...inlineFieldSx['& .MuiOutlinedInput-root'],
                      fontSize: '0.8125rem',
                      color: 'text.secondary'
                    }
                  }}
                />
              </>
            )}
          </Box>

          <Stack direction='row' spacing={0.5} sx={{ pt: 0.5 }}>
            <Tooltip title={isFavourite ? 'Remove from favourites' : 'Add to favourites'}>
              <IconButton onClick={addFavourite} aria-label='Toggle favourite'>
                {isFavourite
                  ? <StarRoundedIcon sx={{ color: 'warning.main' }} />
                  : <StarBorderRoundedIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title='Delete board'>
              <IconButton onClick={() => setConfirmOpen(true)} aria-label='Delete board'>
                <DeleteOutlinedIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Box>

      <Kanban data={sections} boardId={boardId} loading={loading} />

      <ConfirmDialog
        open={confirmOpen}
        title='Delete this board?'
        description='Every section and task on this board will be removed. This cannot be undone.'
        confirmLabel='Delete board'
        onCancel={() => setConfirmOpen(false)}
        onConfirm={deleteBoard}
      />
    </>
  )
}

export default Board
